// SME Capture Flow — Phase 1 (describe) + Phase 2 (review & order).
//
// A standalone route (/capture) separate from the discovery ArcPilotShell.
//  - Describe: the expert narrates freely; the system silently extracts the
//    things that happened and shows them as draft cards.
//  - Review:   those events become a tidied, time-ordered timeline the expert
//    reorders / renames and confirms (confirm rather than recall).

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCapture } from '../hooks/useCapture';
import { moveEntry, renameEntry, TimelineEntry } from '../utils/captureTimeline';
import { StageEntry } from '../utils/captureStages';
import CaptureProblems from './CaptureProblems';
import CaptureAttribution from './CaptureAttribution';
import CaptureStages from './CaptureStages';
import '../styles/CaptureShell.css';

const CaptureShell: React.FC = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const {
    trackId,
    tracks,
    switchTrack,
    addExpert,
    scope,
    saveScope,
    seedFromText,
    events,
    timeline,
    problems,
    attributions,
    nudge,
    busy,
    error,
    loading,
    start,
    describe,
    order,
    saveTimeline,
    flagProblem,
    resolveProblem,
    setAttribution,
    deriveHidden,
    stages,
    proposeStages,
    saveStages,
    toDiscovery,
  } = useCapture(sessionId);
  const [draft, setDraft] = useState('');
  const [view, setView] = useState<'describe' | 'review' | 'attribution' | 'stages'>('describe');
  const [draftTimeline, setDraftTimeline] = useState<TimelineEntry[]>([]);
  const [draftStages, setDraftStages] = useState<StageEntry[]>([]);
  const [saved, setSaved] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [stepError, setStepError] = useState('');
  const [startFailed, setStartFailed] = useState(false);
  const [addingExpert, setAddingExpert] = useState(false);
  const [expertLabel, setExpertLabel] = useState('');
  const [scopeStart, setScopeStart] = useState('');
  const [scopeEnd, setScopeEnd] = useState('');
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedText, setSeedText] = useState('');
  // Ref latch: survives StrictMode's double effect invocation (state guards
  // don't — both runs see the initial-render closure) and, unlike the old
  // setStarting(false)-on-error, never re-arms the effect into an infinite
  // session-creation retry loop. Retry is explicit, via the button below.
  const startRequested = useRef(false);

  // Visiting /capture with no id: create a session + track, then land on it.
  useEffect(() => {
    if (sessionId || startRequested.current) return;
    startRequested.current = true;
    start()
      .then((id) => navigate(`/capture/${id}`, { replace: true }))
      .catch(() => setStartFailed(true));
  }, [sessionId, start, navigate]);

  // Keep the editable copy in sync whenever the server timeline changes
  // (after order() or saveTimeline()). Local edits don't touch `timeline`, so
  // they aren't clobbered.
  useEffect(() => {
    setDraftTimeline(timeline);
  }, [timeline]);

  // Same pattern for stages: the editable copy re-syncs when the server's
  // stages change (after proposeStages() or saveStages()).
  useEffect(() => {
    setDraftStages(stages);
  }, [stages]);

  // And for the scope inputs (Phase 0 framing).
  useEffect(() => {
    setScopeStart(scope.start);
    setScopeEnd(scope.end);
  }, [scope]);

  const onSend = async () => {
    const msg = draft.trim();
    if (!msg) return;
    // Clear only once the turn lands — a failed request must not destroy the
    // expert's narration (the textarea is disabled while busy anyway).
    const ok = await describe(msg);
    if (ok) setDraft('');
  };

  const onAddExpert = async () => {
    const ok = await addExpert(expertLabel.trim());
    if (ok) {
      setExpertLabel('');
      setAddingExpert(false);
    }
  };

  const scopeDirty = scopeStart !== scope.start || scopeEnd !== scope.end;
  const onSaveScope = async () => {
    await saveScope(scopeStart.trim(), scopeEnd.trim());
  };

  const onSeed = async () => {
    // Keep the pasted document on failure — same rule as narration.
    const ok = await seedFromText(seedText);
    if (ok) {
      setSeedText('');
      setSeedOpen(false);
    }
  };

  const onOrganise = async () => {
    setSaved(false);
    setView('review');
    await order();
  };

  // A blank step would be silently deleted by the server's sanitizer — block
  // the save instead and tell the expert which steps need text.
  const blankSteps = () =>
    draftTimeline.reduce<number[]>((acc, e, i) => (e.text.trim() ? acc : [...acc, i + 1]), []);

  const onConfirm = async (): Promise<boolean> => {
    const blanks = blankSteps();
    if (blanks.length) {
      setStepError(`Step${blanks.length > 1 ? 's' : ''} ${blanks.join(', ')} can't be empty.`);
      return false;
    }
    setStepError('');
    const ok = await saveTimeline(draftTimeline);
    setSaved(ok);
    return ok;
  };

  // Save the agreed order, then move on to attribution (who triggers each step).
  const onNext = async () => {
    const ok = await onConfirm();
    if (!ok) return; // a failed save must not advance and drop the edits
    setView('attribution');
  };

  // Build the hidden action→event layer behind the scenes, then move on to
  // confirming the structure. The system proposes the stages — never a blank
  // prompt — so auto-propose on first entry.
  const onStructure = async () => {
    const ok = await deriveHidden();
    if (!ok) return;
    setAllDone(false);
    setView('stages');
    if (stages.length === 0) await proposeStages();
  };

  // Confirm the stages. Blank labels would be silently dropped by the server's
  // sanitizer — block the save and say which ones need a name.
  const onSaveStages = async () => {
    const blanks = draftStages.reduce<number[]>(
      (acc, s, i) => (s.label.trim() ? acc : [...acc, i + 1]),
      [],
    );
    if (blanks.length) {
      setStepError(`Stage${blanks.length > 1 ? 's' : ''} ${blanks.join(', ')} need${blanks.length > 1 ? '' : 's'} a name.`);
      return;
    }
    setStepError('');
    setAllDone(await saveStages(draftStages));
  };

  // Hand off to discovery: build the process map and open it for refining.
  const onToProcessMap = async () => {
    const newId = await toDiscovery();
    if (newId) navigate(`/sessions/${newId}`);
  };

  if (startFailed) {
    return (
      <div className="capture-loading">
        <div>
          <p className="capture-error">Couldn&apos;t start a capture session.</p>
          <button
            onClick={() => {
              setStartFailed(false);
              startRequested.current = false;
              // Re-arm the bootstrap effect by changing no deps: call start
              // directly so retry doesn't depend on effect re-runs.
              startRequested.current = true;
              start()
                .then((id) => navigate(`/capture/${id}`, { replace: true }))
                .catch(() => setStartFailed(true));
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!sessionId || loading) {
    return <div className="capture-loading">Starting capture…</div>;
  }

  // ── Stages (Phase 5) ──────────────────────────────────────────
  if (view === 'stages') {
    return (
      <div className="capture-shell">
        <header className="capture-header">
          <h1>Confirm the stages</h1>
          <p>
            Here&apos;s how the process seems to divide into stages. Rename them or nudge where each
            one starts, then confirm it reads right.
          </p>
        </header>

        {busy && draftStages.length === 0 ? (
          <p className="capture-empty">Suggesting stages…</p>
        ) : draftStages.length === 0 ? (
          <p className="capture-empty">No stages suggested yet.</p>
        ) : (
          <CaptureStages timeline={timeline} stages={draftStages} disabled={busy} onChange={setDraftStages} />
        )}

        {stepError && <p className="capture-error">{stepError}</p>}
        {error && <p className="capture-error">{error}</p>}
        {allDone && !error && (
          <div className="capture-saved">
            All done — your process is captured. Turn it into a process map to refine the detail.
          </div>
        )}

        <CaptureProblems problems={problems} onFlag={flagProblem} onResolve={resolveProblem} />

        <div className="capture-footer">
          <button
            className="capture-secondary"
            onClick={() => {
              setView('attribution');
              setStepError('');
              setAllDone(false);
            }}
          >
            ← Back to who does what
          </button>
          {allDone ? (
            <button onClick={onToProcessMap} disabled={busy}>
              {busy ? 'Building…' : 'Turn this into a process map →'}
            </button>
          ) : (
            <button onClick={onSaveStages} disabled={busy || draftStages.length === 0}>
              {busy ? 'Saving…' : 'Looks right — save'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Attribution (Phase 4) ─────────────────────────────────────
  if (view === 'attribution') {
    return (
      <div className="capture-shell">
        <header className="capture-header">
          <h1>Who makes each step happen?</h1>
          <p>
            For each step, say who kicks it off — a person, or whether it just happens automatically.
            We&apos;ll work out the rest behind the scenes.
          </p>
        </header>

        {timeline.length === 0 ? (
          <p className="capture-empty">Organise the timeline first.</p>
        ) : (
          <CaptureAttribution timeline={timeline} attributions={attributions} onSave={setAttribution} />
        )}

        {error && <p className="capture-error">{error}</p>}

        <CaptureProblems problems={problems} onFlag={flagProblem} onResolve={resolveProblem} />

        <div className="capture-footer">
          <button className="capture-secondary" onClick={() => setView('review')}>
            ← Back to the timeline
          </button>
          <button onClick={onStructure} disabled={busy || timeline.length === 0}>
            {busy ? 'Working…' : 'Confirm the stages →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Review (Phase 2) ──────────────────────────────────────────
  if (view === 'review') {
    return (
      <div className="capture-shell">
        <header className="capture-header">
          <h1>Review the timeline</h1>
          <p>
            Here&apos;s the process in order. Tidy anything that&apos;s off — drag a step up or down, or
            rename it — then confirm it reads right.
          </p>
        </header>

        {busy && draftTimeline.length === 0 ? (
          <p className="capture-empty">Organising the steps…</p>
        ) : draftTimeline.length === 0 ? (
          <p className="capture-empty">Nothing to organise yet.</p>
        ) : (
          <ol className="capture-timeline">
            {draftTimeline.map((e, i) => (
              <li key={e.id} className="capture-timeline-row">
                <span className="capture-timeline-num">{i + 1}</span>
                <input
                  className="capture-timeline-text"
                  value={e.text}
                  disabled={busy}
                  onChange={(ev) => setDraftTimeline(renameEntry(draftTimeline, e.id, ev.target.value))}
                />
                <div className="capture-timeline-actions">
                  <button
                    onClick={() => setDraftTimeline(moveEntry(draftTimeline, i, 'up'))}
                    disabled={busy || i === 0}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => setDraftTimeline(moveEntry(draftTimeline, i, 'down'))}
                    disabled={busy || i === draftTimeline.length - 1}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}

        {stepError && <p className="capture-error">{stepError}</p>}
        {error && <p className="capture-error">{error}</p>}
        {saved && !error && <p className="capture-saved">Saved — this is the agreed order.</p>}

        <CaptureProblems problems={problems} onFlag={flagProblem} onResolve={resolveProblem} />

        <div className="capture-footer">
          <button
            className="capture-secondary"
            onClick={() => {
              setView('describe');
              setSaved(false);
            }}
          >
            ← Back to describing
          </button>
          <button className="capture-secondary" onClick={onConfirm} disabled={busy || draftTimeline.length === 0}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onNext} disabled={busy || draftTimeline.length === 0}>
            Who does what? →
          </button>
        </div>
      </div>
    );
  }

  // ── Describe (Phase 1) ────────────────────────────────────────
  return (
    <div className="capture-shell">
      <header className="capture-header">
        <h1>Describe the process</h1>
        <p>
          Tell the story in your own words — start to finish, in any order. We&apos;ll quietly note what
          happens; you don&apos;t need to organise or label anything yet.
        </p>
      </header>

      {/* Phase 0 — frame: set the boundaries, optionally seed from a document.
          Anything taken from a document is a draft to confirm, not fact. */}
      <section className="capture-frame">
        <div className="capture-frame-scope">
          <input
            value={scopeStart}
            onChange={(e) => setScopeStart(e.target.value)}
            placeholder="Where does the process start? e.g. a customer places an order"
          />
          <input
            value={scopeEnd}
            onChange={(e) => setScopeEnd(e.target.value)}
            placeholder="Where does it end? e.g. the goods are delivered"
          />
          <button onClick={onSaveScope} disabled={!scopeDirty}>
            Set
          </button>
        </div>
        {seedOpen ? (
          <div className="capture-frame-seed">
            <textarea
              value={seedText}
              onChange={(e) => setSeedText(e.target.value)}
              placeholder="Paste an existing procedure or document — we'll pull out a draft list of what happens, for you to confirm."
              rows={4}
            />
            <div className="capture-frame-seed-row">
              <button className="capture-track-cancel" onClick={() => setSeedOpen(false)}>
                Cancel
              </button>
              <button onClick={onSeed} disabled={busy || !seedText.trim()}>
                {busy ? 'Reading…' : 'Use document'}
              </button>
            </div>
          </div>
        ) : (
          <button className="capture-frame-seed-toggle" onClick={() => setSeedOpen(true)}>
            Paste a document to get a head start
          </button>
        )}
      </section>

      {/* Parallel expert tracks: each expert tells the story on their own
          track; ordering reconciles them and flags disagreements. */}
      <div className="capture-tracks">
        {tracks.map((t, i) => (
          <button
            key={t.trackId}
            className={`capture-track-tab${t.trackId === trackId ? ' active' : ''}`}
            onClick={() => switchTrack(t.trackId)}
          >
            {t.expertLabel || `Expert ${i + 1}`}
          </button>
        ))}
        {addingExpert ? (
          <span className="capture-track-add">
            <input
              value={expertLabel}
              onChange={(e) => setExpertLabel(e.target.value)}
              placeholder="Who else knows this process?"
              onKeyDown={(e) => {
                if (e.key === 'Enter') onAddExpert();
                if (e.key === 'Escape') setAddingExpert(false);
              }}
              autoFocus
            />
            <button onClick={onAddExpert}>Add</button>
            <button className="capture-track-cancel" onClick={() => setAddingExpert(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button className="capture-track-new" onClick={() => setAddingExpert(true)}>
            + Add expert
          </button>
        )}
      </div>

      <div className="capture-body">
        <section className="capture-input">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. The customer searches for a product, adds it to their basket, then pays…"
            rows={6}
            disabled={!trackId || busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSend();
            }}
          />
          <div className="capture-input-row">
            <span className="capture-hint">⌘ / Ctrl + Enter to send</span>
            <button onClick={onSend} disabled={!trackId || busy || !draft.trim()}>
              {busy ? 'Listening…' : 'Add to the story'}
            </button>
          </div>
          {nudge && <p className="capture-nudge">{nudge}</p>}
          {error && <p className="capture-error">{error}</p>}
        </section>

        <section className="capture-events">
          <h2>
            What happened
            {events.length > 0 && <span className="capture-count">{events.length}</span>}
          </h2>
          {events.length === 0 ? (
            <p className="capture-empty">Nothing captured yet — start describing on the left.</p>
          ) : (
            <ul className="capture-event-list">
              {events.map((e) => (
                <li key={e.id} className={`capture-event-card${e.fromDocument ? ' from-doc' : ''}`}>
                  {e.text}
                </li>
              ))}
            </ul>
          )}
          {events.length > 0 && (
            <button className="capture-organise" onClick={onOrganise} disabled={busy}>
              Organise into a timeline →
            </button>
          )}
        </section>
      </div>

      <CaptureProblems problems={problems} onFlag={flagProblem} onResolve={resolveProblem} />
    </div>
  );
};

export default CaptureShell;
